<template>
  <LoadingIndicator v-if="isLoading">Loading role...</LoadingIndicator>
  <div v-else class="form-container">
    <div class="form-header">
      <h2>{{ isEditing ? 'Edit Organization Role' : 'Create New Organization Role' }}</h2>
    </div>
    <form @submit.prevent="handleSubmit" class="form">
      <div class="form-section">
        <h3>Role Details</h3>
        <div class="form-group">
          <label for="name">Name</label>
          <input 
            id="name"
            v-model="formData.name"
            type="text"
            required
            placeholder="Enter role name"
            class="form-control"
          />
        </div>

        <div class="form-group full-width">
          <label for="description">Description</label>
          <textarea
            id="description"
            v-model="formData.description"
            placeholder="Enter role description"
            rows="3"
            class="form-control"
          ></textarea>
        </div>
      </div>

      <div class="form-actions">
                <button type="submit" class="submit-button big" :disabled="isSaving">
          {{ isSaving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Role') }}
        </button>
        <button type="button" class="cancel-button medium" @click="handleCancel">
          Cancel
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { computed, reactive, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { getRole, createRole, updateRole, roleKeys } from '@/scripts/api/rolesApi';
import { handleApiError } from '@/scripts/shared-utils';
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const route = useRoute();
const router = useRouter();
const queryClient = useQueryClient();

const roleId = computed(() => route.params.id);
const isEditing = computed(() => !!roleId.value);

const formData = reactive({
  name: '',
  description: ''
});

const { data: roleData, isLoading } = useQuery({
  queryKey: computed(() => roleKeys.getRole(roleId.value)),
  queryFn: () => getRole(roleId.value),
  enabled: isEditing,
  staleTime: 60000,
  onError: (error) => handleApiError(error, 'fetching role'),
});

watch(roleData, (data) => {
  if (data) {
    formData.name = data.name || '';
    formData.description = data.description || '';
  }
}, { immediate: true });

const { mutate, isPending: isSaving } = useMutation({
  mutationFn: (data) => {
    return isEditing.value ? updateRole(roleId.value, data) : createRole(data);
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: roleKeys.getRoles() });
    queryClient.invalidateQueries({ queryKey: roleKeys.getRole(roleId.value) });
    router.push('/admin/permissions');
  },
  onError: (error) => handleApiError(error, isEditing.value ? 'updating role' : 'creating role'),
});

const handleSubmit = () => {
  mutate(formData);
};

const handleCancel = () => {
  router.back();
};
</script>

<style scoped>
@import '@/styles/shared-form.css';
</style>
